import { tokens } from "../theme";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  List,
  ListItemButton,
  Popover,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowDropDown, Search } from "@mui/icons-material";

const MONO_FONT = "ui-monospace, monospace";
// Cap rendered rows; a footer tells the user to narrow the search further.
const RENDER_CAP = 100;
const SEARCH_DEBOUNCE_MS = 150;

// Severity washes: the handoff's token table has no severity scale, so light
// keeps today's hexes and dark reuses the palette's own status pairs.
const SEVERITY_COLORS = (colors) => ({
  CRITICAL: {
    bgcolor: colors.isDark ? colors.errBg : "#FBEAEA",
    color: colors.errText,
  },
  HIGH: {
    bgcolor: colors.isDark ? colors.warnBg : "#FDEEE3",
    color: colors.isDark ? colors.warnText : "#B35B26",
  },
  MEDIUM: {
    bgcolor: colors.isDark ? colors.warnBg : "#FDF8E3",
    color: colors.isDark ? colors.warnFg : "#8A6D1A",
  },
  LOW: {
    bgcolor: colors.isDark ? colors.okBg : "#EAFAEA",
    color: colors.okText,
  },
});

const styles = (colors) => ({
  // Fixed-height trigger: the label lives inside the button text, so the
  // button never grows or wraps no matter how many values are selected.
  trigger: (active) => ({
    height: "34px",
    px: "12px",
    gap: "6px",
    textTransform: "none",
    whiteSpace: "nowrap",
    fontSize: "13px",
    fontWeight: 600,
    ...(active
      ? {
          color: colors.link,
          borderColor: colors.linkSoft,
          bgcolor: colors.tint,
          "&:hover": { bgcolor: colors.tintHover, borderColor: colors.link },
        }
      : { color: "text.secondary", borderColor: "divider" }),
  }),
  // 340px base; grows instead of ellipsizing when options are long.
  paper: {
    minWidth: "340px",
    maxWidth: "640px",
    display: "flex",
    flexDirection: "column",
  },
  searchBox: {
    p: "8px",
    pb: "4px",
    position: "sticky",
    top: 0,
    zIndex: 1,
    bgcolor: "background.paper",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: "12px",
    py: "2px",
  },
  headerLabel: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: "text.secondary",
  },
  clearButton: {
    textTransform: "none",
    fontSize: "12px",
    fontWeight: 600,
    minWidth: 0,
    p: "2px 6px",
  },
  list: { maxHeight: 320, overflowY: "auto", py: 0 },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    py: "2px",
    pl: "4px",
    pr: "12px",
  },
  optionText: {
    fontFamily: MONO_FONT,
    fontSize: "12.5px",
    whiteSpace: "nowrap",
    flex: 1,
  },
  severityChip: (severity) => ({
    height: "18px",
    fontSize: "10px",
    fontWeight: 700,
    ...(SEVERITY_COLORS(colors)[severity?.toUpperCase()] || {
      bgcolor: colors.chipNeuBg,
      color: colors.chipNeuFg,
    }),
  }),
  optionCount: { fontSize: "11px", color: "text.secondary" },
  triggerLabel: { fontSize: "12px", fontWeight: 500, color: "text.secondary" },
  triggerSummary: { fontSize: "12.5px", fontWeight: 600, color: colors.ink },
  footer: {
    px: "12px",
    py: "6px",
    fontSize: "11px",
    color: "text.secondary",
    borderTop: "1px solid",
    borderColor: "divider",
  },
});

// Popover-based multi-select filter with a fixed-height trigger button.
// Selection is controlled by the parent and applies immediately; the popover
// stays open for multi-select. Selected options are pinned above the rest.
// Pass `summary` to render the label muted with the value beside it instead of
// the default "Label · n".
const FilterMultiSelect = ({
  label,
  options = [],
  selected = [],
  onChange,
  itemsNoun = "options",
  getOptionMeta,
  getOptionLabel,
  summary,
  sx,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const s = styles(colors);
  // Options double as their own values, so a display name is opt-in and never
  // replaces the value used for selection.
  const optionLabel = (option) => getOptionLabel?.(option) ?? option;
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const listRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(
      () => setQuery(searchInput.trim().toLowerCase()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  const count = selected.length;
  const open = Boolean(anchorEl);

  const filtered = useMemo(() => {
    // Search both the raw value and its display name, so "148" finds
    // "rel-0.148.0" whichever one the user is looking at.
    const matches = query
      ? options.filter(
          (option) =>
            option.toLowerCase().includes(query) ||
            String(getOptionLabel?.(option) ?? option)
              .toLowerCase()
              .includes(query)
        )
      : options;
    const selectedSet = new Set(selected);
    return [
      ...matches.filter((o) => selectedSet.has(o)),
      ...matches.filter((o) => !selectedSet.has(o)),
    ];
  }, [options, selected, query, getOptionLabel]);

  const visible = filtered.slice(0, RENDER_CAP);
  const hiddenCount = filtered.length - visible.length;

  const toggleOption = (option) =>
    onChange(
      selected.includes(option)
        ? selected.filter((item) => item !== option)
        : [...selected, option]
    );

  const handleClose = () => {
    setAnchorEl(null);
    setSearchInput("");
    setQuery("");
  };

  const focusFirstOption = () =>
    listRef.current?.querySelector('[role="option"]')?.focus();

  const handleOptionKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      event.currentTarget.nextElementSibling?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const prev = event.currentTarget.previousElementSibling;
      if (prev?.getAttribute("role") === "option") prev.focus();
      else searchRef.current?.focus();
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<ArrowDropDown />}
        sx={{ ...s.trigger(count > 0), ...sx }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {summary ? (
          <>
            <Typography component="span" sx={s.triggerLabel}>
              {label}
            </Typography>
            <Typography component="span" sx={s.triggerSummary}>
              {summary}
            </Typography>
          </>
        ) : (
          <>{count > 0 ? `${label} · ${count}` : label}</>
        )}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: s.paper }}
        TransitionProps={{ onEntered: () => searchRef.current?.focus() }}
      >
        <Box sx={s.searchBox}>
          <TextField
            inputRef={searchRef}
            size="small"
            fullWidth
            placeholder={`Search ${options.length} ${itemsNoun}…`}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                focusFirstOption();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={s.headerRow}>
          <Typography sx={s.headerLabel}>SELECTED · {count}</Typography>
          <Button
            sx={s.clearButton}
            disabled={count === 0}
            onClick={() => onChange([])}
          >
            Clear
          </Button>
        </Box>
        <List
          ref={listRef}
          role="listbox"
          aria-multiselectable="true"
          aria-label={label}
          sx={s.list}
        >
          {visible.map((option) => {
            const isSelected = selected.includes(option);
            const meta = getOptionMeta?.(option);
            return (
              <ListItemButton
                key={option}
                role="option"
                aria-selected={isSelected}
                dense
                sx={s.option}
                onClick={() => toggleOption(option)}
                onKeyDown={handleOptionKeyDown}
              >
                <Checkbox
                  size="small"
                  checked={isSelected}
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: "4px" }}
                />
                <Typography component="span" sx={s.optionText}>
                  {optionLabel(option)}
                </Typography>
                {meta?.severity ? (
                  <Chip
                    size="small"
                    label={meta.severity}
                    sx={s.severityChip(meta.severity)}
                  />
                ) : null}
                {meta?.count != null ? (
                  <Typography component="span" sx={s.optionCount}>
                    {meta.count}
                  </Typography>
                ) : null}
              </ListItemButton>
            );
          })}
        </List>
        {hiddenCount > 0 ? (
          <Typography sx={s.footer}>
            {hiddenCount} more — keep typing to narrow
          </Typography>
        ) : null}
      </Popover>
    </>
  );
};

export default FilterMultiSelect;
